import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { Button, Spinner } from '@/shared/ui';

import { templatesModel } from '@/entities/templates';

import {
  getLanguageLabel,
  transcriptorRecordingMessages,
} from '@/features/audioRecording';
import { useLocalizedTemplates } from '@/features/templateList';

import type { GalleryItem } from '../../config';
import { useRecordingSetup } from '../../hooks';
import { buildGalleryItems, sortGalleryItems } from '../../lib';
import { RecordingSettingsPanel } from '../RecordingSettingsPanel/RecordingSettingsPanel';
import { TemplateGallery } from '../TemplateGallery/TemplateGallery';

import styles from './TranscriptorStart.module.scss';

type Props = {
  className?: string;
  onSelect: (item: GalleryItem) => void;
};

// The Transcriptor modal: the template and the microphone. A chosen template
// opens on its own start page.
export const TranscriptorStart: FC<Props> = ({ className, onSelect }) => {
  const { formatMessage, locale } = useIntl();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const schemasLoading = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasLoading,
  );

  const {
    language,
    languageOptions,
    microphone,
    soundCheckStatus,
    setLanguage,
  } = useRecordingSetup();
  const listed = useLocalizedTemplates();
  const items = sortGalleryItems(
    buildGalleryItems({
      schemas: listed.schemas,
      templates: listed.documentTemplates,
    }),
  );

  const microphoneAccess =
    microphone.hasPermission === true ? undefined : (
      <Button
        variant="secondary"
        size="small"
        onClick={() => {
          if (microphone.isPermanentlyDenied) {
            window.location.reload();
            return;
          }
          void microphone.checkMicrophonePermission();
        }}
      >
        {microphone.isPermanentlyDenied ? (
          <FormattedMessage
            id="global.refreshPage"
            defaultMessage="Refresh page"
          />
        ) : (
          <FormattedMessage
            id="transcriptor.requestMicrophoneButton"
            defaultMessage="Request microphone access"
          />
        )}
      </Button>
    );

  return (
    <div className={cn(styles.content, className)}>
      <RecordingSettingsPanel
        open={settingsOpen}
        language={language}
        languageOptions={languageOptions.map((value) => ({
          value,
          label: getLanguageLabel(value, locale),
        }))}
        microphone={{
          options: microphone.devices.map((device) => ({
            value: device.deviceId,
            label: device.label,
          })),
          value: microphone.selectedDevice,
          level: microphone.audioLevel,
          disabled: microphone.hasPermission !== true,
          access: microphoneAccess,
          status:
            soundCheckStatus === 'idle'
              ? undefined
              : formatMessage(
                  soundCheckStatus === 'detected'
                    ? transcriptorRecordingMessages.soundCheckDetected
                    : soundCheckStatus === 'no-sound'
                      ? transcriptorRecordingMessages.soundCheckNoSound
                      : transcriptorRecordingMessages.soundCheckChecking,
                ),
          onChange: microphone.setSelectedDevice,
        }}
        onToggle={() => setSettingsOpen((open) => !open)}
        onLanguageChange={setLanguage}
      />
      {microphone.permissionError && (
        <p role="alert" className={cn('p3', styles.alert)}>
          {microphone.permissionError}
        </p>
      )}
      {schemasLoading && !items.length ? (
        <p role="status" className={cn('p3', styles.progress)}>
          <Spinner size={24} />
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        </p>
      ) : (
        <TemplateGallery
          // A new language is a new set of cards.
          key={listed.contentLanguage}
          items={items}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};
